/**
 * Timeline Event Component
 * VOY-12: Individual event card in timeline
 */

import { Button } from '@/components/ui/button';
import { Car, Plane, Hotel, Utensils, Compass, Info, Check } from 'lucide-react';
import { TimelineEvent as TimelineEventType } from '@/data/companion-timeline';

interface TimelineEventProps {
  event: TimelineEventType;
}

const iconMap = {
  car: Car,
  plane: Plane,
  hotel: Hotel,
  restaurant: Utensils,
  activity: Compass,
  info: Info
};

export function TimelineEvent({ event }: TimelineEventProps) {
  const Icon = iconMap[event.icon];
  
  const getBorderColor = () => {
    if (event.status === 'completed') return 'border-gray-300 dark:border-zinc-700';
    if (event.status === 'now') return 'border-[#5B6FED] shadow-md';
    return 'border-[#5B6FED]/30 dark:border-blue-500/30';
  };

  const getOpacity = () => {
    return event.status === 'completed' ? 'opacity-60' : 'opacity-100';
  };

  return (
    <div 
      className={`bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl rounded-3xl p-5 border-l-4 ${getBorderColor()} ${getOpacity()} transition-all hover:shadow-xl hover:shadow-purple-500/10 hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl`}
    >
      {/* Header: Icon + Time */}
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          event.status === 'now' 
            ? 'bg-[#5B6FED] text-white' 
            : 'bg-[#E8E8FF] dark:bg-zinc-700 text-[#5B6FED] dark:text-blue-400'
        }`}>
          <Icon className="w-5 h-5" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-600 dark:text-zinc-400">{event.time}</span>
            {event.status === 'completed' && (
              <div className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                <Check className="w-3 h-3" />
                <span>Completed</span>
              </div>
            )}
            {event.status === 'now' && (
              <div className="flex items-center gap-1">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#5B6FED] opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#5B6FED]"></span>
                </span>
                <span className="text-xs font-semibold text-[#5B6FED]">NOW</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Title & Description */}
      <div className="mb-3">
        <h4 className="font-semibold text-lg text-gray-900 dark:text-white mb-1">
          {event.title}
        </h4>
        <p className="text-sm text-gray-600 dark:text-zinc-400">
          {event.description}
        </p>
      </div>

      {/* Context Info */}
      {event.context && (
        <div className="mb-4 space-y-1">
          {event.context.traffic && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>🚗</span>
              <span>{event.context.traffic}</span>
            </div>
          )}
          {event.context.weather && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>🌤️</span>
              <span>{event.context.weather}</span>
            </div>
          )}
          {event.context.price && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>💰</span>
              <span>{event.context.price}</span>
            </div>
          )}
          {event.context.location && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400">
              <span>📍</span>
              <span>{event.context.location}</span>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {event.actions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {event.actions.map((action, index) => (
            <Button
              key={index}
              onClick={action.onClick}
              variant={action.variant === 'outline' ? 'outline' : 'default'}
              size="sm"
              className={
                action.variant === 'outline'
                  ? 'rounded-full border-[#5B6FED] dark:border-blue-500 text-[#5B6FED] dark:text-blue-400 hover:bg-[#E8E8FF] dark:hover:bg-zinc-700'
                  : 'rounded-full bg-[#5B6FED] hover:bg-[#4A5FDD] text-white'
              }
            >
              {action.label}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

